import { FC, ReactNode } from 'react';
import { Select, SelectProps } from 'antd';
import styled from 'styled-components';
import { FormField, useGetIdFromName } from '../../../common/form';
import { FormSelectSelectionPlaceholder } from '../../../common/form/input/FormSelectSelectionPlaceholder';
import { i18nNsFactory } from '@atlas-frontend/i18n';
import type { AlgorithmMappingQueryDtoType } from '../mappings/schema/types';
import {
  AlgorithmSourceDimensionFormDataType,
  AlgorithmSourceFormDataType,
} from '../sources/schema/types';
import translations from './AlgorithmJoinItemSelectField.translations.json';

const { addTranslations, useTranslation } = i18nNsFactory(
  'algorithm-join-item-select-field-for-categories',
);
addTranslations(translations);

export interface AlgorithmJoinItemSelectFieldOptionGroup {
  key: string;
  label: string;
  options: AlgorithmJoinItemSelectFieldOption[];
}

interface AlgorithmJoinItemSelectFieldBaseOption {
  key: string;
  value: string | number;
  label: string;
  displayLabel: ReactNode;
}

export type AlgorithmJoinItemSelectFieldOption =
  | AlgorithmJoinItemSelectFieldBaseOption
  | (AlgorithmJoinItemSelectFieldBaseOption & {
      algorithmSource: AlgorithmSourceFormDataType;
      dimension: AlgorithmSourceDimensionFormDataType;
    })
  | (AlgorithmJoinItemSelectFieldBaseOption & {
      mapping: AlgorithmMappingQueryDtoType;
    });

export interface AlgorithmJoinItemSelectFieldProps
  extends Omit<SelectProps, 'id' | 'onChange'> {
  options: (
    | AlgorithmJoinItemSelectFieldOptionGroup
    | AlgorithmJoinItemSelectFieldOption
  )[];
  name:
    | `joins.${number}.algorithmJoinItems.${number}.${'source' | 'target'}${'Value' | 'Dimension'}`
    | `joins.${number}.algorithmJoinItems.${number}.algorithmMappingAlias`;
  label: string;
  value: string | number;
  onSelect: Required<SelectProps<string | number, any>>['onSelect'];
  onClear: Required<SelectProps<string | number, any>>['onClear'];
}

export const AlgorithmJoinItemSelectField: FC<
  AlgorithmJoinItemSelectFieldProps
> = (props) => {
  const { options, name, label, value, onSelect, onClear, loading, ...rest } =
    props;
  const { t } = useTranslation();
  const getIdFromName = useGetIdFromName();

  return (
    <StyledFormField required name={name} label={label}>
      <StyledSelect
        id={getIdFromName(name)}
        value={loading ? null : value}
        onSelect={onSelect}
        onClear={onClear}
        styles={{
          popup: { root: { width: 'max-content' } },
        }}
        allowClear
        optionLabelProp="displayLabel"
        placeholder={
          <FormSelectSelectionPlaceholder>
            {t('placeholder')}
          </FormSelectSelectionPlaceholder>
        }
        options={options}
        loading={loading}
        {...rest}
      />
    </StyledFormField>
  );
};

const StyledFormField = styled(FormField)`
  min-width: 0;

  > div {
    align-items: center;
    flex-grow: 1;
  }
`;

const StyledSelect = styled(Select<string | number, any>)`
  height: auto;
`;
